
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

interface LoadingStateProps {
  onRetry?: () => void;
}

const LoadingState: React.FC<LoadingStateProps> = ({ onRetry }) => {
  const [loadingTime, setLoadingTime] = useState(0);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const startTime = Date.now();
    
    // Update loading time every second
    const interval = setInterval(() => {
      setLoadingTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    // Show error message after 10 seconds of loading
    const errorTimer = setTimeout(() => {
      setShowError(true);
    }, 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(errorTimer);
    };
  }, []);

  return (
    <div className="flex flex-col items-center justify-center p-8 w-full">
      {showError && (
        <div className="mb-6 w-full bg-red-500 text-white p-4 rounded-md shadow-md text-center">
          <div className="font-semibold text-lg mb-1">Sync failed</div>
          <div>Some data could not be synced. Please try again.</div>
          {onRetry && (
            <Button 
              onClick={onRetry}
              className="mt-3 bg-white text-red-500 hover:bg-gray-100"
            >
              Try Again
            </Button>
          )}
        </div>
      )}
      
      <div className="flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-300">
            Loading your dashboard... ({loadingTime}s)
          </p>
          {loadingTime > 15 && !showError && (
            <div className="mt-4 flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertCircle size={16} />
              <p className="text-sm">
                This is taking longer than expected.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingState;
